import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type BreadcrumbItem = {
    label: ReactNode;
    to?: string;
};

type BreadcrumbProps = {
    items: BreadcrumbItem[];
    /** Optional leading back affordance. `backTo` renders a Link; `onBack` a button. */
    backTo?: string;
    onBack?: () => void;
    backLabel?: string;
    'aria-label'?: string;
    className?: string;
};

export default function Breadcrumb({
    items,
    backTo,
    onBack,
    backLabel = 'Voltar',
    'aria-label': ariaLabel = 'Navegação',
    className,
}: BreadcrumbProps) {
    const back =
        backTo !== undefined ? (
            <Button
                asChild
                variant="ghost"
                size="iconSm"
                aria-label={backLabel}
                className="-ml-1 shrink-0 text-muted-foreground"
            >
                <Link to={backTo}>
                    <ArrowLeft aria-hidden="true" className="size-4" />
                </Link>
            </Button>
        ) : onBack ? (
            <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={onBack}
                aria-label={backLabel}
                className="-ml-1 shrink-0 text-muted-foreground"
            >
                <ArrowLeft aria-hidden="true" className="size-4" />
            </Button>
        ) : null;

    if (items.length === 0 && !back) return null;

    return (
        <nav aria-label={ariaLabel} className={cn('flex items-center gap-1', className)}>
            {back}
            <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                {items.map((item, index) => (
                    <Fragment key={index}>
                        {index > 0 && (
                            <ArrowRight2 aria-hidden="true" className="size-3.5 shrink-0" />
                        )}
                        <li className="min-w-0">
                            {item.to ? (
                                <Link
                                    to={item.to}
                                    className="font-medium text-muted-foreground transition-colors hover:text-foreground hover:underline"
                                >
                                    {item.label}
                                </Link>
                            ) : (
                                <span
                                    aria-current="page"
                                    className="block max-w-64 truncate font-medium text-foreground sm:max-w-none"
                                >
                                    {item.label}
                                </span>
                            )}
                        </li>
                    </Fragment>
                ))}
            </ol>
        </nav>
    );
}
